/**
 * useWorkspacePanel.ts
 *
 * Fetches the lightweight panel payload from GET /api/workspaces/:id/panel —
 * workspace row + quick_stats only. Used by the On the Record context panel so
 * the Active Workspace and Coverage Period cards render without waiting on the
 * heavier /detail payload (suggested questions, corpus stats, tool_runs).
 *
 * Contract:
 *   - data is null while loading and on hard error.
 *   - data.quick_stats has coverage_from / coverage_to / dated_docs as
 *     numbers/strings per the QuickStats type.
 */

import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '@/lib/apiFetch';
import type { QuickStats } from '@/types/workspaceDetail';

interface WorkspacePanelData {
  workspace: Record<string, unknown> | null;
  quick_stats: QuickStats | null;
  suggested_questions: string[];
  warnings: string[];
}

interface UseWorkspacePanelResult {
  data: WorkspacePanelData | null;
  loading: boolean;
  /** Hard error (network failure, 4xx/5xx). */
  error: string | null;
  refetch: () => void;
}

export function useWorkspacePanel(
  workspaceId: string | undefined
): UseWorkspacePanelResult {
  const [data, setData] = useState<WorkspacePanelData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!workspaceId) return;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await apiFetch(`/api/workspaces/${workspaceId}/panel`);

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            (body as { error?: string }).error ??
              `Failed to load workspace panel (${res.status})`
          );
        }

        const json: {
          workspace?: Record<string, unknown> | null;
          quick_stats?: QuickStats | null;
          suggested_questions?: string[];
          warnings?: string[];
        } = await res.json();

        if (!cancelled) {
          setData({
            workspace: json.workspace ?? null,
            quick_stats: json.quick_stats ?? null,
            suggested_questions: json.suggested_questions ?? [],
            warnings: json.warnings ?? [],
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to load workspace panel'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [workspaceId, tick]);

  return { data, loading, error, refetch };
}
